import type { Artist, Booking, CustomerRecord, Studio } from '@/domain/types';

/**
 * ICS calendar invite generator (Prompt I3, 09 Jul ruling). Pure + deterministic:
 * the invite is attached to the booking confirmation so the appointment lands in
 * the client's own calendar (opt-out friction). No timezone library — DTSTART/DTEND
 * are emitted as UTC instants derived from the offset already baked into the stored
 * ISO, so they are unambiguous without a VTIMEZONE block. The studio's IANA zone
 * travels as X-WR-TIMEZONE for display.
 */

const UID_DOMAIN = 'addikted-to-ink';

/** VIC/NSW/QLD/WA studio -> IANA zone. */
// ASSUMPTION: Australia/Melbourne for VIC studios, studio-mapped otherwise.
export function studioTimezone(studio: Studio): string {
  switch (studio.state) {
    case 'NSW':
      return 'Australia/Sydney';
    case 'QLD':
      return 'Australia/Brisbane';
    case 'WA':
      return 'Australia/Perth';
    case 'VIC':
    default:
      return 'Australia/Melbourne';
  }
}

/** ISO -> `YYYYMMDDTHHMMSSZ` (UTC basic format). */
function toUtcStamp(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

/** Escape a value per RFC 5545 (backslash, comma, semicolon, newline). */
function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

export interface BookingInviteOptions {
  /** Chain-root UID so a rescheduled booking keeps the original invite identity. */
  uid?: string;
  /** Reschedule depth; bumped so calendars treat the invite as an update. */
  sequence?: number;
  /** DTSTAMP source (the confirmation time); passed in to stay pure/deterministic. */
  dtstampIso?: string;
}

export function buildBookingInvite(
  booking: Booking,
  _customer: CustomerRecord,
  _artist: Artist | undefined,
  studio: Studio,
  opts: BookingInviteOptions = {},
): string {
  const uid = opts.uid ?? `${booking.id}@${UID_DOMAIN}`;
  const sequence = opts.sequence ?? 0;
  const dtstamp = toUtcStamp(opts.dtstampIso ?? booking.createdAt);
  const dtstart = toUtcStamp(booking.start);
  const dtend = toUtcStamp(
    new Date(new Date(booking.start).getTime() + booking.durationMin * 60_000).toISOString(),
  );

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:-//${UID_DOMAIN}//booking invite//EN`,
    'METHOD:REQUEST',
    'CALSCALE:GREGORIAN',
    `X-WR-TIMEZONE:${studioTimezone(studio)}`,
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `SEQUENCE:${sequence}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${dtstart}`,
    `DTEND:${dtend}`,
    `SUMMARY:${escapeText(`Tattoo session - ${studio.name}`)}`,
    `LOCATION:${escapeText(studio.address)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return `${lines.join('\r\n')}\r\n`;
}
